/**
 * PURCHASE ORDERS, AND WHAT ACTUALLY ARRIVED.
 *
 * The road is draft → approved → sent, and each step is a different person's
 * job. A draft's lines can be set; an approved order's cannot — it is amended,
 * which makes a new version and leaves the old one readable, because the
 * supplier has the old one.
 *
 * A RECEIPT records what came, what was rejected and why — three numbers, not
 * one. "Received 40" when six were cracked is a lie the account will repeat
 * every month. Over-receipt is refused: more than was ordered arriving is a
 * conversation with the supplier, not a quantity to type in.
 */
import * as React from 'react';
import { useState } from 'react';
import { api, can, type Session } from '../api.js';
import { useAsk, useRun, go, routeQuery } from '../hooks.js';
import { Banner, Button, Card, Loading, Money, Pill, Register, Select, Text, TextArea, type Column } from '../ui.js';
import { Pager } from '../pager.js';
import { Page } from '../shell.js';
import { SupplierPickerById } from '../pickers.js';
import { count, date, rupeesToPaise, today, words } from '../format.js';
import { screenById } from '../../../src/screens/contracts.js';

const MESSAGES = screenById('purchases')!.messages;
interface PoLine { description: string; uom: string; qty: string; rupees: string }

export function Purchases({ id, session }: { id: string | null; session: Session }) {
  if (id === 'new') return <NewPurchase session={session} />;
  if (id) return <OnePurchase purchaseOrderId={Number(id)} session={session} />;
  return <PurchaseRegister session={session} />;
}

function PurchaseRegister({ session }: { session: Session }) {
  const supplierFromRoute = routeQuery('supplier');
  const [status, setStatus] = useState('');
  const [supplierId, setSupplierId] = useState(supplierFromRoute ?? '');
  const [page, setPage] = useState(1);
  const asked = useAsk(() => api.view('purchases.list', {
    ...(status ? { status: status as 'draft' | 'approved' | 'sent' | 'cancelled' | 'superseded' } : {}),
    ...(supplierId ? { supplierId: Number(supplierId) } : {}), page, pageSize: 50,
  }), [status, supplierId, page], MESSAGES);

  const columns: readonly Column<{ purchaseOrderId: number; number: string | null; supplierId: number; supplierName: string; status: string; orderedOn: string; expectedOn: string | null; versionNo: number; lines: number; totalPaise: number; outstanding: number; overdue: boolean }>[] = [
    { key: 'number', head: 'Order', cell: (r) => <>{r.number ? <strong>{r.number}</strong> : <span className="muted">draft</span>}{r.versionNo > 1 ? <div className="muted">version {r.versionNo}</div> : null}</> },
    { key: 'supplier', head: 'Supplier', cell: (r) => r.supplierName },
    { key: 'ordered', head: 'Ordered', cell: (r) => date(r.orderedOn) },
    { key: 'expected', head: 'Expected', cell: (r) => r.expectedOn
        ? <>{date(r.expectedOn)}{r.overdue ? <div><Pill tone="danger">overdue</Pill></div> : null}</> : <span className="muted">—</span> },
    { key: 'lines', head: 'Lines', right: true, cell: (r) => count(r.lines) },
    { key: 'out', head: 'Still owed', right: true, cell: (r) => r.outstanding > 0 ? <strong>{count(r.outstanding)}</strong> : <span className="muted">none</span> },
    { key: 'total', head: 'Value', right: true, cell: (r) => <Money paise={r.totalPaise} /> },
    { key: 'status', head: 'Status', cell: (r) => <Pill tone={r.status === 'sent' ? 'good' : r.status === 'cancelled' ? 'danger' : r.status === 'draft' ? 'empty' : 'primary'}>{r.status}</Pill> },
  ];

  return (
    <Page title="Purchase orders" sub="What we have ordered, what is still owed, and what arrived."
          actions={<>
            <Button onClick={() => go('#/suppliers')}>Suppliers</Button>
            {can(session, 'po.raise') ? <Button kind="primary" onClick={() => go('#/purchases/new')}>Raise an order</Button> : null}
          </>}>
      <div className="toolbar">
        <Select name="status" label="Status" value={status} onChange={(v) => { setStatus(v); setPage(1); }} placeholder="Any status"
                options={['draft', 'approved', 'sent', 'cancelled', 'superseded'].map((v) => ({ value: v, label: words(v) }))} />
        <SupplierPickerById name="supplier" label="Supplier" supplierId={supplierId}
                            onPick={(v) => setSupplierId(v)} placeholder="Any supplier" />
      </div>
      <Card>
        {asked.loading ? <Loading what="purchase orders" /> : asked.error ? <Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} />
          : (<>
              <Register columns={columns} rows={asked.data!.rows} empty="No purchase order matches that."
                        onOpen={(r) => go(`#/purchases/${r.purchaseOrderId}`)} asOf={new Date().toISOString()} />
              <Pager page={asked.data!.page} onPage={setPage} />
            </>)}
      </Card>
    </Page>
  );
}

function OnePurchase({ purchaseOrderId, session }: { purchaseOrderId: number; session: Session }) {
  const asked = useAsk(() => api.view('purchases.get', { purchaseOrderId }), [purchaseOrderId], MESSAGES);
  const runner = useRun(MESSAGES);
  const [receiving, setReceiving] = useState(false);
  const [amending, setAmending] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [sending, setSending] = useState(false);
  const [editing, setEditing] = useState(false);
  const [reason, setReason] = useState('');
  const [sentOn, setSentOn] = useState(today());

  if (asked.loading) return <Page title="Purchase order"><Loading what="the order" /></Page>;
  if (asked.error) return <Page title="Purchase order"><Banner kind="error" title={asked.error.title} {...(asked.error.detail ? { detail: asked.error.detail } : {})} /></Page>;
  const d = asked.data!;
  const o = d.order;
  const draft = o.status === 'draft';
  const done = () => { setReceiving(false); setAmending(false); setCancelling(false); setSending(false); setEditing(false); asked.reload(); };

  async function approve() { const ok = await runner.run((retryKey) => api.command('po.approve', { purchaseOrderId }, retryKey ? { idempotencyKey: retryKey } : {})); if (ok) asked.reload(); }
  async function send() { const ok = await runner.run(() => api.command('po.send', { purchaseOrderId, sentOn })); if (ok) done(); }
  async function cancel() { const ok = await runner.run(() => api.command('po.cancel', { purchaseOrderId, reason: reason.trim() })); if (ok) done(); }

  return (
    <Page title={o.number ? `Purchase order ${o.number}` : `Draft order #${o.purchaseOrderId}`}
          sub={`${o.supplierName} · ordered ${date(o.orderedOn)}${o.versionNo > 1 ? ` · version ${o.versionNo}` : ''}`}
          actions={<>
            <Button onClick={() => go('#/suppliers')}>The supplier</Button>
            {draft && can(session, 'po.raise') ? <Button onClick={() => setEditing((v) => !v)}>{editing ? 'Leave the lines' : 'Change the lines'}</Button> : null}
            {draft && can(session, 'po.approve') ? <Button kind="primary" onClick={() => { void approve(); }} disabled={runner.busy}>Approve it</Button> : null}
            {o.status === 'approved' && can(session, 'po.send') ? <Button kind="primary" onClick={() => setSending((v) => !v)}>{sending ? 'Not yet' : 'Send it'}</Button> : null}
            {o.status === 'sent' && can(session, 'receipt.record') ? <Button kind="primary" onClick={() => setReceiving((v) => !v)}>{receiving ? 'Cancel' : 'Record a receipt'}</Button> : null}
            {(o.status === 'approved' || o.status === 'sent') && can(session, 'po.amend') ? <Button onClick={() => setAmending((v) => !v)}>{amending ? 'Keep it' : 'Amend it'}</Button> : null}
            {o.status !== 'cancelled' && can(session, 'po.cancel') ? <Button kind="danger" onClick={() => setCancelling((v) => !v)}>{cancelling ? 'Keep it' : 'Cancel'}</Button> : null}
          </>}>

      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} {...(runner.error.reference ? { reference: runner.error.reference } : {})} /> : null}

      {o.status === 'approved' ? <Banner kind="info" title="Approved, and not yet sent." detail="The supplier does not have it until it is sent." /> : null}
      {o.status === 'cancelled' ? <Banner kind="warn" title="This order is cancelled." {...(o.cancelReason ? { detail: o.cancelReason } : {})} /> : null}
      {o.supersededById ? <Banner kind="info" title="This version has been amended." detail={`Version ${o.versionNo} is superseded — open the current one to see what stands.`} /> : null}

      {editing ? <Lines purchaseOrderId={purchaseOrderId} lines={d.lines} onDone={done} /> : null}
      {editing ? <Charges purchaseOrderId={purchaseOrderId} order={d.order} onDone={done} /> : null}
      {sending ? (
        <Card title="Send it to the supplier">
          <Text name="sent-on" label="Sent on" type="date" value={sentOn} onChange={setSentOn} required />
          <Button kind="primary" onClick={() => { void send(); }} disabled={runner.busy || !sentOn}>Send it</Button>
        </Card>
      ) : null}
      {cancelling ? (
        <Card title="Cancel this order" sub="what has already arrived stays received">
          <TextArea name="cancel-reason" label="Why" value={reason} onChange={setReason} />
          <Button kind="danger" onClick={() => { void cancel(); }} disabled={runner.busy || !reason.trim()}>Cancel it</Button>
        </Card>
      ) : null}
      {amending ? <Amend purchaseOrderId={purchaseOrderId} lines={d.lines} expectedOn={o.expectedOn} onDone={done} /> : null}
      {receiving ? <Receive purchaseOrderId={purchaseOrderId} lines={d.lines} onDone={done} /> : null}

      <Card title="What was ordered" sub="ordered, received, rejected and still owed — four facts, not one">
        <Register
          columns={[
            { key: 'line', head: 'Line', cell: (l: { lineNo: number }) => `#${l.lineNo}` },
            { key: 'what', head: 'What', cell: (l: { description: string; uom: string }) => <>{l.description}<span className="muted"> · {l.uom}</span></> },
            { key: 'qty', head: 'Ordered', right: true, cell: (l: { qtyOrdered: number; qtyCancelled: number }) => <>{count(l.qtyOrdered)}{l.qtyCancelled ? <div className="muted">{count(l.qtyCancelled)} cancelled</div> : null}</> },
            { key: 'rate', head: 'Rate', right: true, cell: (l: { ratePaise: number }) => <Money paise={l.ratePaise} /> },
            { key: 'recd', head: 'Received', right: true, cell: (l: { received: number; overReceived: boolean }) => <>{count(l.received)}{l.overReceived ? <div><Pill tone="danger">more than ordered</Pill></div> : null}</> },
            { key: 'rej', head: 'Rejected', right: true, cell: (l: { rejected: number }) => l.rejected ? <strong style={{ color: 'var(--danger)' }}>{count(l.rejected)}</strong> : <span className="muted">—</span> },
            { key: 'out', head: 'Still owed', right: true, cell: (l: { outstanding: number }) => <strong>{count(l.outstanding)}</strong> },
            { key: 'amount', head: 'Amount', right: true, cell: (l: { amountPaise: number }) => <Money paise={l.amountPaise} /> },
          ]}
          rows={d.lines} empty="Nothing is on this order."
          totals={<span>Value <b><Money paise={o.totalPaise} /></b> · still owed <b>{count(o.outstanding)}</b></span>} />
      </Card>

      {d.receipts.length ? (
        <Card title="What arrived" sub="each delivery, and the supplier's own document for it">
          {d.receipts.map((r) => (
            <div key={r.receiptId} style={{ borderTop: '1px solid var(--border)', paddingTop: 12, marginTop: 12 }}>
              <strong>{date(r.receivedOn)}</strong> <span className="muted">· {r.supplierDocument}{r.vehicle ? ` · ${r.vehicle}` : ''}</span>
              <Register
                columns={[
                  { key: 'line', head: 'Line', cell: (l: { purchaseOrderLineId: number }) => `#${d.lines.find((x) => x.purchaseOrderLineId === l.purchaseOrderLineId)?.lineNo ?? l.purchaseOrderLineId}` },
                  { key: 'recd', head: 'Received', right: true, cell: (l: { qtyReceived: number }) => count(l.qtyReceived) },
                  { key: 'rej', head: 'Rejected', right: true, cell: (l: { qtyRejected: number; rejectReason: string | null }) => l.qtyRejected
                      ? <>{count(l.qtyRejected)}{l.rejectReason ? <div className="muted">{l.rejectReason}</div> : null}</> : <span className="muted">—</span> },
                ]}
                rows={r.lines} empty="none" />
            </div>
          ))}
        </Card>
      ) : null}

      {d.versions.length > 1 ? (
        <Card title="Every version" sub="an amended order leaves the old one readable — the supplier has that one">
          <Register
            columns={[
              { key: 'v', head: 'Version', cell: (v: { versionNo: number }) => `v${v.versionNo}` },
              { key: 'number', head: 'Number', cell: (v: { number: string | null }) => v.number ?? <span className="muted">draft</span> },
              { key: 'status', head: 'Status', cell: (v: { status: string }) => <Pill>{v.status}</Pill> },
              { key: 'why', head: 'Amended because', cell: (v: { amendReason: string | null }) => v.amendReason ?? <span className="muted">—</span> },
            ]}
            rows={d.versions} empty="none" onOpen={(v) => go(`#/purchases/${v.purchaseOrderId}`)} />
        </Card>
      ) : null}
    </Page>
  );
}

function Lines({ purchaseOrderId, lines, onDone }: {
  purchaseOrderId: number;
  lines: readonly { description: string; uom: string; qtyOrdered: number; ratePaise: number }[];
  onDone: () => void;
}) {
  const [draft, setDraft] = useState<PoLine[]>(() => lines.length
    ? lines.map((l) => ({ description: l.description, uom: l.uom, qty: String(l.qtyOrdered), rupees: (l.ratePaise / 100).toFixed(2) }))
    : [{ description: '', uom: 'nos', qty: '1', rupees: '' }]);
  const runner = useRun(MESSAGES);
  function setLine(i: number, patch: Partial<PoLine>) { setDraft((o) => o.map((l, n) => (n === i ? { ...l, ...patch } : l))); }
  const bad = draft.some((l) => l.description.trim() && rupeesToPaise(l.rupees) === null);

  async function submit() {
    const ok = await runner.run(() => api.command('po.setLines', {
      purchaseOrderId,
      lines: draft.filter((l) => l.description.trim() && Number(l.qty) > 0).map((l) => ({
        description: l.description.trim(), uom: l.uom.trim(), qty: Number(l.qty), ratePaise: rupeesToPaise(l.rupees) ?? 0,
      })),
    }));
    if (ok) onDone();
  }

  return (
    <Card title="What we are ordering" sub="the whole list is restated — a line left out is a line removed">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {bad ? <Banner kind="warn" title="One of the rates is not money." detail="Rupees, with at most two decimals." /> : null}
      {draft.map((l, i) => (
        <div className="toolbar" key={i}>
          <div className="field grow"><label htmlFor={`pdesc-${i}`}>What</label>
            <input id={`pdesc-${i}`} type="text" value={l.description} onChange={(e: { target: { value: string } }) => setLine(i, { description: e.target.value })} /></div>
          <div className="field"><label htmlFor={`puom-${i}`}>Unit</label>
            <input id={`puom-${i}`} type="text" value={l.uom} onChange={(e: { target: { value: string } }) => setLine(i, { uom: e.target.value })} /></div>
          <div className="field"><label htmlFor={`pqty-${i}`}>Quantity</label>
            <input id={`pqty-${i}`} type="number" value={l.qty} onChange={(e: { target: { value: string } }) => setLine(i, { qty: e.target.value })} /></div>
          <div className="field"><label htmlFor={`prate-${i}`}>Rate (₹)</label>
            <input id={`prate-${i}`} type="text" value={l.rupees} onChange={(e: { target: { value: string } }) => setLine(i, { rupees: e.target.value })} /></div>
          {draft.length > 1 ? <Button small kind="danger" onClick={() => setDraft((o) => o.filter((_, n) => n !== i))}>Remove</Button> : null}
        </div>
      ))}
      <Button small onClick={() => setDraft((o) => [...o, { description: '', uom: 'nos', qty: '1', rupees: '' }])}>Add another line</Button>
      <Button kind="primary" wide onClick={() => { void submit(); }} disabled={runner.busy || bad || draft.every((l) => !l.description.trim())}>
        {runner.busy ? 'Saving…' : 'Save these lines'}
      </Button>
    </Card>
  );
}

function Amend({ purchaseOrderId, lines, expectedOn, onDone }: {
  purchaseOrderId: number;
  lines: readonly { purchaseOrderLineId: number; lineNo: number; description: string; qtyOrdered: number; ratePaise: number }[];
  expectedOn: string | null; onDone: () => void;
}) {
  const [qty, setQty] = useState<Record<number, string>>(() => Object.fromEntries(lines.map((l) => [l.purchaseOrderLineId, String(l.qtyOrdered)])));
  const [expected, setExpected] = useState(expectedOn ?? '');
  const [reason, setReason] = useState('');
  const runner = useRun(MESSAGES);
  async function submit() {
    const ok = await runner.run((retryKey) => api.command('po.amend', {
      purchaseOrderId, reason: reason.trim(),
      ...(expected ? { expectedOn: expected } : {}),
      lines: lines.map((l) => ({ purchaseOrderLineId: l.purchaseOrderLineId, qty: Number(qty[l.purchaseOrderLineId] ?? l.qtyOrdered) })),
    }, retryKey ? { idempotencyKey: retryKey } : {}));
    if (ok) onDone();
  }
  return (
    <Card title="Amend this order" sub="a new version; the one the supplier already has stays readable">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {lines.map((l) => (
        <div className="toolbar" key={l.purchaseOrderLineId}>
          <span className="grow">#{l.lineNo} <strong>{l.description}</strong> <span className="muted">was {count(l.qtyOrdered)}</span></span>
          <div className="field"><label htmlFor={`aqty-${l.purchaseOrderLineId}`}>Now</label>
            <input id={`aqty-${l.purchaseOrderLineId}`} type="number" value={qty[l.purchaseOrderLineId] ?? ''}
                   onChange={(e: { target: { value: string } }) => setQty((o) => ({ ...o, [l.purchaseOrderLineId]: e.target.value }))} /></div>
        </div>
      ))}
      <Text name="amend-expected" label="Expected" type="date" value={expected} onChange={setExpected} />
      <TextArea name="amend-reason" label="Why it is being amended" value={reason} onChange={setReason} />
      <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !reason.trim()}>
        {runner.busy ? 'Amending…' : 'Make a new version'}
      </Button>
    </Card>
  );
}

function Receive({ purchaseOrderId, lines, onDone }: {
  purchaseOrderId: number;
  lines: readonly { purchaseOrderLineId: number; lineNo: number; description: string; outstanding: number }[];
  onDone: () => void;
}) {
  const [receivedOn, setReceivedOn] = useState(today());
  const [document, setDocument] = useState(''); const [vehicle, setVehicle] = useState('');
  const [got, setGot] = useState<Record<number, string>>({});
  const [rej, setRej] = useState<Record<number, string>>({});
  const [why, setWhy] = useState<Record<number, string>>({});
  const runner = useRun(MESSAGES);

  const entered = lines.filter((l) => Number(got[l.purchaseOrderLineId] ?? 0) > 0 || Number(rej[l.purchaseOrderLineId] ?? 0) > 0);
  const over = entered.filter((l) => Number(got[l.purchaseOrderLineId] ?? 0) > l.outstanding);

  async function submit() {
    const ok = await runner.run(() => api.command('receipt.record', {
      purchaseOrderId, receivedOn, supplierDocument: document.trim(),
      ...(vehicle.trim() ? { vehicle: vehicle.trim() } : {}),
      lines: entered.map((l) => ({
        purchaseOrderLineId: l.purchaseOrderLineId,
        qtyReceived: Number(got[l.purchaseOrderLineId] ?? 0),
        ...(Number(rej[l.purchaseOrderLineId] ?? 0) > 0 ? { qtyRejected: Number(rej[l.purchaseOrderLineId]) } : {}),
        ...(why[l.purchaseOrderLineId]?.trim() ? { rejectReason: why[l.purchaseOrderLineId]!.trim() } : {}),
      })),
    }));
    if (ok) onDone();
  }

  return (
    <Card title="Record what arrived" sub="received and rejected are different numbers — “received 40” when six were cracked is a lie the account repeats every month">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      {over.length ? (
        <Banner kind="warn" title="That is more than the order still has outstanding."
                detail="More arriving than was ordered is a conversation with the supplier, not a quantity to type in — the command refuses it." />
      ) : null}
      <div className="grid two">
        <div><Text name="recd-on" label="Received on" type="date" value={receivedOn} onChange={setReceivedOn} required />
             <Text name="recd-doc" label="Their document" value={document} onChange={setDocument} required help="the supplier's own challan or invoice number" /></div>
        <div><Text name="recd-vehicle" label="Vehicle" value={vehicle} onChange={setVehicle} /></div>
      </div>
      {lines.map((l) => (
        <div className="toolbar" key={l.purchaseOrderLineId}>
          <span className="grow">#{l.lineNo} <strong>{l.description}</strong> <span className="muted">{count(l.outstanding)} still owed</span></span>
          <div className="field"><label htmlFor={`got-${l.purchaseOrderLineId}`}>Received</label>
            <input id={`got-${l.purchaseOrderLineId}`} type="number" value={got[l.purchaseOrderLineId] ?? ''}
                   onChange={(e: { target: { value: string } }) => setGot((o) => ({ ...o, [l.purchaseOrderLineId]: e.target.value }))} /></div>
          <div className="field"><label htmlFor={`rej-${l.purchaseOrderLineId}`}>Rejected</label>
            <input id={`rej-${l.purchaseOrderLineId}`} type="number" value={rej[l.purchaseOrderLineId] ?? ''}
                   onChange={(e: { target: { value: string } }) => setRej((o) => ({ ...o, [l.purchaseOrderLineId]: e.target.value }))} /></div>
          {Number(rej[l.purchaseOrderLineId] ?? 0) > 0 ? (
            <div className="field grow"><label htmlFor={`why-${l.purchaseOrderLineId}`}>Why rejected</label>
              <input id={`why-${l.purchaseOrderLineId}`} type="text" value={why[l.purchaseOrderLineId] ?? ''}
                     onChange={(e: { target: { value: string } }) => setWhy((o) => ({ ...o, [l.purchaseOrderLineId]: e.target.value }))} /></div>
          ) : null}
        </div>
      ))}
      <Button kind="primary" wide onClick={() => { void submit(); }} disabled={runner.busy || !document.trim() || entered.length === 0 || over.length > 0}>
        {runner.busy ? 'Recording…' : entered.length === 0 ? 'Nothing entered yet' : `Record what arrived on ${count(entered.length)} line(s)`}
      </Button>
    </Card>
  );
}

/**
 * 0181 — A SUPPLIER, ADDED WITHOUT LEAVING THE ORDER.
 *
 * The owner: "to create a new supplier, adding a Supplier is difficult". It was: you left
 * the order, went to Suppliers, made one, and came back to an empty form. This makes one
 * here and selects it.
 *
 * The fields are the ones a purchase order has to print or the office has to telephone —
 * the GSTIN, where they are registered, where the goods come from, somebody to ring, and
 * what we agreed to pay on. Only the name is required: a supplier entered in a hurry with
 * a telephone number is still a supplier, and a form demanding eleven fields is one people
 * work around by typing the name into the notes.
 */
/**
 * 0181 — THE CHARGES ON A DRAFT. Freight is quoted after the order is drafted more often
 * than with it, so they are changed here without rewriting a single line. A draft only:
 * once an order is approved, its money is what somebody approved, and changing it
 * afterwards is an amendment, which this screen already offers.
 */
function Charges({ purchaseOrderId, order, onDone }: {
  purchaseOrderId: number;
  order: { freightPaise: number; loadingPaise: number; unloadingPaise: number; otherPaise: number; otherChargeNote: string | null };
  onDone: () => void;
}) {
  const asRupees = (p: number) => (p ? String(p / 100) : '');
  const [freight, setFreight] = useState(asRupees(order.freightPaise));
  const [loading, setLoading] = useState(asRupees(order.loadingPaise));
  const [unloading, setUnloading] = useState(asRupees(order.unloadingPaise));
  const [other, setOther] = useState(asRupees(order.otherPaise));
  const [otherNote, setOtherNote] = useState(order.otherChargeNote ?? '');
  const runner = useRun(MESSAGES);

  async function save() {
    const ok = await runner.run(() => api.command('purchase.setCharges', {
      purchaseOrderId,
      freightPaise: rupeesToPaise(freight) ?? 0,
      loadingPaise: rupeesToPaise(loading) ?? 0,
      unloadingPaise: rupeesToPaise(unloading) ?? 0,
      otherPaise: rupeesToPaise(other) ?? 0,
      otherChargeNote: otherNote.trim(),
    }));
    if (ok) onDone();
  }

  return (
    <Card title="What it costs beyond the goods" sub="delivery, loading, unloading, and anything else the supplier charges">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="toolbar">
        <div className="field"><label htmlFor="c-freight">Delivery / freight (₹)</label>
          <input id="c-freight" type="text" value={freight} onChange={(e: { target: { value: string } }) => setFreight(e.target.value)} /></div>
        <div className="field"><label htmlFor="c-loading">Loading (₹)</label>
          <input id="c-loading" type="text" value={loading} onChange={(e: { target: { value: string } }) => setLoading(e.target.value)} /></div>
        <div className="field"><label htmlFor="c-unloading">Unloading (₹)</label>
          <input id="c-unloading" type="text" value={unloading} onChange={(e: { target: { value: string } }) => setUnloading(e.target.value)} /></div>
        <div className="field"><label htmlFor="c-other">Other (₹)</label>
          <input id="c-other" type="text" value={other} onChange={(e: { target: { value: string } }) => setOther(e.target.value)} /></div>
      </div>
      {rupeesToPaise(other) ? (
        <Text name="c-other-note" label="What the other charge is for" value={otherNote} onChange={setOtherNote} required
              help="an unexplained figure is one nobody can query when the invoice arrives" />
      ) : null}
      <Button kind="primary" wide onClick={() => { void save(); }} disabled={runner.busy}>
        {runner.busy ? 'Saving…' : 'Save the charges'}
      </Button>
    </Card>
  );
}

function NewSupplierInline({ onMade }: { onMade: (supplierId: number) => void }) {
  const [name, setName] = useState(''); const [gstin, setGstin] = useState('');
  const [registered, setRegistered] = useState(''); const [delivery, setDelivery] = useState('');
  const [contact, setContact] = useState(''); const [phone, setPhone] = useState('');
  const [email, setEmail] = useState(''); const [terms, setTerms] = useState('');
  const [supplies, setSupplies] = useState('');
  const runner = useRun(MESSAGES);

  async function save() {
    const ok = await runner.run(async () => {
      const r = await api.command('supplier.create', {
        name: name.trim(),
        ...(supplies.trim() ? { supplies: supplies.trim() } : {}),
        ...(gstin.trim() ? { gstin: gstin.trim().toUpperCase() } : {}),
        ...(registered.trim() ? { registeredAddress: registered.trim() } : {}),
        ...(delivery.trim() ? { deliveryAddress: delivery.trim() } : {}),
        ...(contact.trim() ? { contactPerson: contact.trim() } : {}),
        ...(phone.trim() ? { phone: phone.trim() } : {}),
        ...(email.trim() ? { email: email.trim() } : {}),
        ...(terms.trim() ? { paymentTerms: terms.trim() } : {}),
      });
      const v = r.value as { supplierId?: number };
      if (v?.supplierId) onMade(v.supplierId);
      return r;
    });
    if (!ok) return;
  }

  return (
    <Card title="A new supplier" sub="only the name is needed — the rest can be filled in later, and prints on the order when it is">
      {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
      <div className="grid two">
        <div>
          <Text name="sup-name" label="Name" value={name} onChange={setName} required />
          <Text name="sup-gstin" label="GSTIN" value={gstin} onChange={setGstin} help="fifteen characters, like 18AABCU9603R1ZM" />
          <Text name="sup-contact" label="Who to ask for" value={contact} onChange={setContact} />
          <Text name="sup-phone" label="Telephone" value={phone} onChange={setPhone} />
        </div>
        <div>
          <Text name="sup-email" label="Email" value={email} onChange={setEmail} />
          <Text name="sup-terms" label="Payment terms" value={terms} onChange={setTerms} help="in their words — 30 days from invoice, 50% advance" />
          <Text name="sup-supplies" label="What they supply" value={supplies} onChange={setSupplies} />
        </div>
      </div>
      <TextArea name="sup-reg" label="Registered address" value={registered} onChange={setRegistered}
                help="the address the purchase order is made out to" />
      <TextArea name="sup-del" label="Delivery address" value={delivery} onChange={setDelivery}
                help="where the goods actually come from, when it is not the registered one" />
      <Button kind="primary" onClick={() => { void save(); }} disabled={runner.busy || !name.trim()}>
        {runner.busy ? 'Saving…' : 'Add the supplier'}
      </Button>
    </Card>
  );
}

function NewPurchase({ session }: { session: Session }) {
  const [supplierId, setSupplierId] = useState('');
  const [orderedOn, setOrderedOn] = useState(today()); const [expectedOn, setExpectedOn] = useState('');
  const [reference, setReference] = useState(''); const [notes, setNotes] = useState('');
  const [lines, setLines] = useState<PoLine[]>([{ description: '', uom: 'nos', qty: '1', rupees: '' }]);
  // 0181 — the owner: "the delivery cost loading, unloading cost, other charges, if any".
  // They decide what we actually pay and had nowhere to go.
  const [freight, setFreight] = useState(''); const [loading, setLoading] = useState('');
  const [unloading, setUnloading] = useState(''); const [other, setOther] = useState('');
  const [otherNote, setOtherNote] = useState('');
  // 0181 — and: "adding a Supplier is difficult". It happens here now, not by leaving.
  const [adding, setAdding] = useState(false);
  const runner = useRun(MESSAGES);
  function setLine(i: number, patch: Partial<PoLine>) { setLines((o) => o.map((l, n) => (n === i ? { ...l, ...patch } : l))); }
  const bad = lines.some((l) => l.description.trim() && rupeesToPaise(l.rupees) === null);

  async function submit() {
    const ok = await runner.run(async () => {
      const r = await api.command('po.raise', {
        supplierId: Number(supplierId), orderedOn,
        ...(expectedOn ? { expectedOn } : {}),
        ...(reference.trim() ? { supplierReference: reference.trim() } : {}),
        ...(notes.trim() ? { notes: notes.trim() } : {}),
        ...(rupeesToPaise(freight) ? { freightPaise: rupeesToPaise(freight)! } : {}),
        ...(rupeesToPaise(loading) ? { loadingPaise: rupeesToPaise(loading)! } : {}),
        ...(rupeesToPaise(unloading) ? { unloadingPaise: rupeesToPaise(unloading)! } : {}),
        ...(rupeesToPaise(other) ? { otherPaise: rupeesToPaise(other)!, otherChargeNote: otherNote.trim() } : {}),
        lines: lines.filter((l) => l.description.trim() && Number(l.qty) > 0).map((l) => ({
          description: l.description.trim(), uom: l.uom.trim(), qty: Number(l.qty), ratePaise: rupeesToPaise(l.rupees) ?? 0,
        })),
      });
      const v = r.value as { purchaseOrderId?: number };
      if (v?.purchaseOrderId) go(`#/purchases/${v.purchaseOrderId}`);
      return r;
    });
    if (!ok) return;
  }

  return (
    <Page title="Raise a purchase order" sub="A draft — it is approved by somebody else, and sent after that.">
      <Card>
        {runner.error ? <Banner kind="error" title={runner.error.title} {...(runner.error.detail ? { detail: runner.error.detail } : {})} /> : null}
        {bad ? <Banner kind="warn" title="One of the rates is not money." detail="Rupees, with at most two decimals." /> : null}
        <div className="grid two">
          <div>
            <SupplierPickerById name="supplier" label="Supplier" supplierId={supplierId}
                                onPick={(v) => setSupplierId(v)} placeholder="Choose a supplier" />
            {can(session, 'supplier.manage')
              ? <Button small onClick={() => setAdding((v) => !v)}>{adding ? 'Cancel the new supplier' : 'New supplier'}</Button>
              : null}
            {adding ? <NewSupplierInline onMade={(sid) => { setAdding(false); setSupplierId(String(sid)); }} /> : null}
            <Text name="ordered-on" label="Ordered on" type="date" value={orderedOn} onChange={setOrderedOn} required />
          </div>
          <div>
            <Text name="expected-on" label="Expected" type="date" value={expectedOn} onChange={setExpectedOn} />
            <Text name="supplier-ref" label="Their reference" value={reference} onChange={setReference} />
          </div>
        </div>
        <h3 className="section">What we are ordering</h3>
        {lines.map((l, i) => (
          <div className="toolbar" key={i}>
            <div className="field grow"><label htmlFor={`ndesc-${i}`}>What</label>
              <input id={`ndesc-${i}`} type="text" value={l.description} onChange={(e: { target: { value: string } }) => setLine(i, { description: e.target.value })} /></div>
            <div className="field"><label htmlFor={`nuom-${i}`}>Unit</label>
              <input id={`nuom-${i}`} type="text" value={l.uom} onChange={(e: { target: { value: string } }) => setLine(i, { uom: e.target.value })} /></div>
            <div className="field"><label htmlFor={`nqty-${i}`}>Quantity</label>
              <input id={`nqty-${i}`} type="number" value={l.qty} onChange={(e: { target: { value: string } }) => setLine(i, { qty: e.target.value })} /></div>
            <div className="field"><label htmlFor={`nrate-${i}`}>Rate (₹)</label>
              <input id={`nrate-${i}`} type="text" value={l.rupees} onChange={(e: { target: { value: string } }) => setLine(i, { rupees: e.target.value })} /></div>
            {lines.length > 1 ? <Button small onClick={() => setLines((o) => o.filter((_, n) => n !== i))}>Remove</Button> : null}
          </div>
        ))}
        <Button small onClick={() => setLines((o) => [...o, { description: '', uom: 'nos', qty: '1', rupees: '' }])}>Add another line</Button>
        <h3 className="section">What it costs beyond the goods</h3>
        <p className="muted" style={{ marginTop: -4 }}>
          The way a supplier quotes it: so much for the goods, so much to bring them, so much
          to get them off the lorry. Leave a box empty if it does not apply.
        </p>
        <div className="toolbar">
          <div className="field"><label htmlFor="po-freight">Delivery / freight (₹)</label>
            <input id="po-freight" type="text" value={freight} onChange={(e: { target: { value: string } }) => setFreight(e.target.value)} /></div>
          <div className="field"><label htmlFor="po-loading">Loading (₹)</label>
            <input id="po-loading" type="text" value={loading} onChange={(e: { target: { value: string } }) => setLoading(e.target.value)} /></div>
          <div className="field"><label htmlFor="po-unloading">Unloading (₹)</label>
            <input id="po-unloading" type="text" value={unloading} onChange={(e: { target: { value: string } }) => setUnloading(e.target.value)} /></div>
          <div className="field"><label htmlFor="po-other">Other (₹)</label>
            <input id="po-other" type="text" value={other} onChange={(e: { target: { value: string } }) => setOther(e.target.value)} /></div>
        </div>
        {rupeesToPaise(other) ? (
          <Text name="po-other-note" label="What the other charge is for" value={otherNote} onChange={setOtherNote} required
                help="an unexplained figure is one nobody can query when the invoice arrives" />
        ) : null}
        <TextArea name="po-notes" label="Notes" value={notes} onChange={setNotes} />
        <Button kind="primary" onClick={() => { void submit(); }} disabled={runner.busy || !supplierId || bad || lines.every((l) => !l.description.trim())}>
          {runner.busy ? 'Raising…' : 'Raise the order'}
        </Button>
      </Card>
    </Page>
  );
}
